import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPageHelp } from '../pageHelp';

/**
 * Użyteczny nagłówek strony: tytuł + opis + opcjonalne akcje, statystyki i pomoc.
 */
export default function PageHeader({ title, description, icon, stats, actions, breadcrumb, helpKey, helpContext }) {
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const help = useMemo(() => (helpKey ? getPageHelp(helpKey, helpContext) : null), [helpKey, helpContext]);

  useEffect(() => {
    if (!helpOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setHelpOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [helpOpen]);

  return (
    <>
      <header className="page-header">
        {breadcrumb && (
          <nav className="page-breadcrumb">
            {breadcrumb.map((b, i) => (
              <span key={i}>
                {b.to ? (
                  <a onClick={() => navigate(b.to)}>
                    {b.label}
                  </a>
                ) : (
                  <span className="current">{b.label}</span>
                )}
                {i < breadcrumb.length - 1 && <span className="sep">›</span>}
              </span>
            ))}
          </nav>
        )}
        <div className="page-header-main">
          <div className="page-header-text">
            <h1>
              {icon && <span className="page-header-icon">{icon}</span>}
              {title}
            </h1>
            {description && <p className="page-header-desc">{description}</p>}
          </div>
          <div className="page-header-actions">
            {actions}
            {help && (
              <button type="button" className="secondary page-help-trigger" onClick={() => setHelpOpen(true)}>
                ℹ️ Info
              </button>
            )}
          </div>
        </div>
        {stats && stats.length > 0 && (
          <div className="page-header-stats">
            {stats.map((s, i) => (
              <div key={i} className={`page-stat ${s.tone ? `page-stat-${s.tone}` : ''}`}>
                <span className="page-stat-label">{s.label}</span>
                <strong className="page-stat-value">{s.value}</strong>
              </div>
            ))}
          </div>
        )}
      </header>

      {helpOpen && help && (
        <div className="modal-overlay" onClick={() => setHelpOpen(false)} role="presentation">
          <div className="modal-card page-help-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="page-help-title">
            <div className="modal-header">
              <div>
                <p className="page-help-kicker">Jak działa ta strona</p>
                <h2 id="page-help-title">{help.title || title}</h2>
              </div>
              <button type="button" className="btn-close" onClick={() => setHelpOpen(false)} aria-label="Zamknij pomoc">✕</button>
            </div>
            <div className="modal-body page-help-body">
              {help.intro && <p className="page-help-intro">{help.intro}</p>}
              {(help.sections || []).map((section, idx) => (
                <section key={idx} className="page-help-section">
                  <h3>{section.heading}</h3>
                  {section.paragraphs?.map((paragraph, pIdx) => (
                    <p key={pIdx}>{paragraph}</p>
                  ))}
                  {section.bullets?.length > 0 && (
                    <ul>
                      {section.bullets.map((bullet, bIdx) => <li key={bIdx}>{bullet}</li>)}
                    </ul>
                  )}
                </section>
              ))}
              {help.note && <p className="page-help-note">{help.note}</p>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
